import React, { useEffect, useState } from "react";

import Config from "../components/Config";
import MainGraph from "../components/plots/MainGraph";
import Statistics from "../components/Statistics";
import Login from "./Login";
import Dialog from "../components/CustomDialog";
import CriterionsTable from "../components/CriterionsTable";

import "../App.css";
import "bootstrap/dist/css/bootstrap.min.css";

export interface GraphType {
  x: number[];
  y: number[];
  name: string;
  [key: string]: any;
}

// const colors = [
//   ["#0000FF", "#87CEFA", "#1E90FF", "#000080", "#4169E1", "#00BFFF"],
//   ["#7CFC00", "#ADFF2F", "#006400", "#008000", "#00FA9A", "#3CB371"],
//   ["#8B0000", "#B22222", "#FF0000", "#FF6347", "#F08080", "#FF4500"],
// ];

export interface TableDataType {
  [key: number]: {
    idx: number;
    dut: number;
    start: number;
    low: number;
    high: number;
    lowZyklus: number;
    highZyklus?: number | undefined;
    errorCode?: number;
    error?: string;
    errorZyklus?: number;
  };
}

const compressData = (
  data: number[],
  chunk = 10,
  isPercent: boolean
): number[] => {
  if (chunk < 2) {
    return data;
  }
  let count = 0;
  let sum = 0;
  let result: number[] = [];
  let x = data[0];
  if (isPercent) {
    data = data.map((e) => (e / x) * 100);
  }
  data.forEach((e, i) => {
    sum += e;
    count += 1;
    if (count === chunk || i === data.length - 1) {
      result.push(sum / count);
      count = 0;
      sum = 0;
    }
  });
  return result;
};

const getLine = (
  graph: GraphType,
  percent: number,
  color: string,
  showLegend = false
): GraphType => {
  return {
    ...graph,
    name: `${percent}%`,
    x: [graph.x[0], graph.x[graph.x.length - 1]],
    y: [(graph.y[0] * percent) / 100, (graph.y[0] * percent) / 100],
    line: {
      width: 2,
      color: color,
      dash: "dash",
    },
    mode: "lines",
    showlegend: showLegend,
  };
};

function Home() {
  const [data, setData] = useState<{ [id: number]: any }>({});
  const [chunk, setChunk] = useState(100);
  const [yValue, setYValue] = useState<null | string>("");
  const [yValues, setYValues] = useState<string[]>([]);
  const [isPercent, setIsPercent] = useState(false);
  const [isShowedAll, setIsShowedAll] = useState(true);
  const [configCustomFailures, setConfigCustomFailures] = useState(false);
  const [isShowedTable, setIsShowedTable] = useState(false);
  const [failureCriterion, setFailureCriterion] = useState(110);
  const [user, setUser] = useState<any>();
  const [maxX, setMaxX] = useState(0);

  const [failureData, setFailureData] = useState<{ [key: string]: any }[]>([]);
  const [criterions, setCriterions] = useState<
    { key: string; value: number; set: boolean }[]
  >([
    { key: "VF_END", value: 110, set: false },
    { key: "DELTA_TVM", value: 110, set: false },
    { key: "RTH", value: 110, set: false },
  ]);
  const [customFailures, setCustomFailures] = useState<any>({});

  const [tableData, setTableData] = useState<TableDataType[]>([]);

  const [graphs, setGraphs] = useState<GraphType[]>([]);

  useEffect(() => {
    if (!(yValue && data)) {
      return;
    }
    let result: GraphType[] = [];
    let x = 0;
    let table: any = {};
    Object.keys(data).forEach((key) => {
      let tmpKey = parseInt(key);
      let id =
        tmpKey < 10 ? tmpKey % 10 : tmpKey < 20 ? tmpKey - 4 : tmpKey - 8;
      x = Math.max(...data[tmpKey].ZYKLUS, x);
      let start = data[tmpKey][yValue][0];
      let index = data[tmpKey][yValue].findIndex(
        (e: any) => e > (start * failureCriterion) / 100
      );
      table[tmpKey] = {
        dut: tmpKey,
        error: data[tmpKey].EREIGNIS[data[tmpKey].EREIGNIS.length - 1],
        errorZyklus: data[tmpKey].ZYKLUS[data[tmpKey].ZYKLUS.length - 1],
        failureZyklus: index ? data[tmpKey].ZYKLUS[index] : null,
      };
      let tmp = {
        x: compressData(data[tmpKey].ZYKLUS, chunk, false),
        y: compressData(data[tmpKey][yValue], chunk, isPercent),
        name: `DUT ${key}`,
        idx: tmpKey,
        xaxis: isShowedAll ? "x1" : `x${id}`,
        yaxis: isShowedAll ? "y1" : `y${id}`,
        line: {
          color: tmpKey < 10 ? "blue" : tmpKey < 20 ? "green" : "red",
          width: 1,
        },
      };
      result.push(tmp);
      if (!isShowedAll) {
        result.push(getLine(tmp, failureCriterion, "#261C2C", false));
      }
    });
    if (isShowedAll && isPercent) {
      result.push(getLine(result[0], failureCriterion, "#261C2C", true));
    }
    setMaxX(x);
    setGraphs(result);
    setTableData(table);
  }, [data, yValue, chunk, isPercent, isShowedAll, failureCriterion]);

  if (!user) {
    return <Login setUser={setUser} />;
  }

  return (
    <div className="App" style={{ overflowX: "hidden" }}>
      <Config
        setData={setData}
        setYValue={setYValue}
        chunk={chunk}
        setChunk={setChunk}
        isPercent={isPercent}
        setIsPercent={setIsPercent}
        isShowedAll={isShowedAll}
        setIsShowedAll={setIsShowedAll}
        isShowedTable={isShowedTable}
        setIsShowedTable={setIsShowedTable}
        failureCriterion={failureCriterion}
        setFailureCriterion={setFailureCriterion}
        yValues={yValues}
        setYValues={setYValues}
        setConfigCustomFailures={setConfigCustomFailures}
      />
      {isShowedTable ? (
        <Statistics
          customFailures={customFailures}
          setCustomFailures={setCustomFailures}
          criterions={criterions}
          setCriterions={setCriterions}
          failureData={failureData}
          setFailureData={setFailureData}
          data={data}
          tableData={tableData}
          yValue={yValue}
          yValues={yValues}
          failureCriterion={failureCriterion}
        />
      ) : (
        <MainGraph
          graphs={graphs}
          isShowedAll={isShowedAll}
          yValue={yValue}
          maxX={maxX}
          isPercent={isPercent}
        />
      )}
      <Dialog
        open={configCustomFailures}
        setOpen={setConfigCustomFailures}
        title="Custom Failure Criterion Definition"
      >
        <CriterionsTable
          criterions={criterions}
          setCriterions={setCriterions}
        />
      </Dialog>
    </div>
  );
}

export default Home;
